import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { Heart, Copy, Download, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { mediaUrl, thumbUrl, api, apiError } from "@/api";
import { loadPng } from "@/lib/pngCache";
import { PALETTE, swatchStyle, sameColor, tintPng } from "@/lib/tint";
import { StickerPreview } from "@/components/StickerPreview";
import { StickerHelpSheet } from "@/components/StickerHelpSheet";
import { displayName } from "@/lib/stickerName";
import { copyPng, downloadPng, isIOS } from "@/lib/shareSticker";

const ORIGINAL_STYLE = { backgroundImage: "conic-gradient(#E8A0AE, #F4E0A3, #BDD8C0, #B7D2EB, #CDB9E4, #E8A0AE)" };

// Uploaded PNGs first (or the original), then the auto-painted palette
function buildOptions(sticker) {
  const uploaded = Array.isArray(sticker.variations) && sticker.variations.length
    ? sticker.variations.map((v) => ({ ...v, path: v.storage_path }))
    : [{ key: "original", label: "Original", hex: null, path: sticker.storage_path, original: true }];
  const norm = (t) => (t || "").trim().toLowerCase();
  const auto = PALETTE.filter((c) => !uploaded.some((u) => norm(u.label) === norm(c.label) || (u.hex && sameColor(u.hex, c.hex)))).map((c) => ({ ...c, tint: true }));
  return { options: [...uploaded, ...auto], baseUrl: mediaUrl(uploaded[0].path), baseThumb: thumbUrl(uploaded[0].path) };
}

// Full-quality PNG (copy, download, big preview)
const getPng = (opt, baseUrl) => (opt.tint ? tintPng(baseUrl, opt) : loadPng(mediaUrl(opt.path)));
// Light preview for the grid
const getThumb = (opt, baseThumb) => (opt.tint ? tintPng(baseThumb, opt) : Promise.resolve({ objectUrl: thumbUrl(opt.path) }));

function Swatches({ sticker, options, selKey, onSelect, prefix = "sticker" }) {
  return (
    <div
      className="mt-1.5 -mx-1 px-2 py-1.5 flex items-center gap-2 sm:gap-1.5 overflow-x-auto scrollbar-hide snap-x"
      data-testid={`${prefix}-swatches-${sticker.id}`}
    >
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onSelect(o.key)}
          data-testid={`${prefix}-swatch-${sticker.id}-${o.key}`}
          title={o.label}
          aria-label={o.label}
          className={`h-7 w-7 sm:h-6 sm:w-6 shrink-0 snap-start rounded-full border transition-transform duration-150 active:scale-90 ${
            selKey === o.key ? "ring-2 ring-offset-2 ring-[#C58B90] border-white" : "border-black/15"
          }`}
          style={o.original ? ORIGINAL_STYLE : o.hex ? swatchStyle(o) : undefined}
        />
      ))}
    </div>
  );
}

function StickerCardBase({ sticker, isFav, onToggleFav }) {
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [help, setHelp] = useState({ open: false, blob: null, copied: false });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [bgKey, setBgKey] = useState("queimado");
  const { options, baseUrl, baseThumb } = useMemo(() => buildOptions(sticker), [sticker]);
  const [selKey, setSelKey] = useState(options[0].key);
  const current = options.find((o) => o.key === selKey) || options[0];
  const cardRef = useRef(null);
  const [urls, setUrls] = useState({});
  const [fullUrls, setFullUrls] = useState({});
  const [loadedSrc, setLoadedSrc] = useState(null);
  const fileName = `${sticker.name || "sticker"}-${current.label}.png`;

  useEffect(() => {
    if (current.key === options[0].key || urls[current.key]) return;
    let alive = true;
    getThumb(current, baseThumb)
      .then((x) => alive && setUrls((u) => ({ ...u, [current.key]: x.objectUrl })))
      .catch(() => alive && toast.error("Não foi possível carregar esta cor."));
    return () => { alive = false; };
  }, [current, baseThumb, options, urls]);

  useEffect(() => {
    if (!previewOpen || fullUrls[current.key]) return;
    let alive = true;
    getPng(current, baseUrl)
      .then((x) => alive && setFullUrls((u) => ({ ...u, [current.key]: x.objectUrl })))
      .catch(() => {});
    return () => { alive = false; };
  }, [previewOpen, current, baseUrl, fullUrls]);

  const firstSrc = thumbUrl(options[0].path);
  const wanted = current.key === options[0].key ? firstSrc : urls[current.key];
  const [shown, setShown] = useState(firstSrc);
  useEffect(() => { if (wanted) setShown(wanted); }, [wanted]);
  const loading = !wanted || loadedSrc !== shown;

  const handleDownload = async () => {
    try {
      const { blob } = await getPng(current, baseUrl);
      downloadPng(blob, fileName);
      toast.success("Sticker baixado! 💗");
    } catch (e) {
      toast.error("Não foi possível baixar. Tente novamente.");
    }
  };

  // iPhone: copy and paste in Instagram. Android & others: copy too, then offer share/save, since Instagram there often can't paste images
  const handleCopy = async () => {
    setCopying(true);
    const blobPromise = getPng(current, baseUrl).then((x) => x.blob);
    let ok = false;
    try {
      await copyPng(blobPromise);
      ok = true;
    } catch (e) {
      ok = false;
    }
    try {
      if (ok && isIOS) {
        setCopied(true);
        toast.success(`Copiado em ${current.label}! Cole direto nos seus Stories ✨`);
        setTimeout(() => setCopied(false), 1800);
        return;
      }
      const blob = await blobPromise;
      setHelp({ open: true, blob, copied: ok });
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      }
    } catch (e) {
      toast.error("Não foi possível carregar a figurinha. Tente novamente.");
    } finally {
      setCopying(false);
    }
  };

  const toggleFav = async () => {
    try {
      if (isFav) await api.delete(`/favorites/${sticker.id}`);
      else await api.post(`/favorites/${sticker.id}`);
      onToggleFav && onToggleFav(sticker.id, !isFav);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div
      data-testid={`sticker-card-${sticker.id}`}
      ref={cardRef}
      className="relative h-full flex flex-col bg-white rounded-3xl p-2.5 sm:p-3 border border-[#F2E6E7] bc-shadow-soft"
      style={{ contentVisibility: "auto", containIntrinsicSize: "300px" }}
    >
      {sticker.is_new && (
        <span
          data-testid={`sticker-new-badge-${sticker.id}`}
          className="absolute top-2 left-2 z-10 bg-[#C58B90] text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-widest"
        >
          Novo
        </span>
      )}
      <button
        data-testid={`sticker-fav-${sticker.id}`}
        onClick={toggleFav}
        className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full bg-white/90 flex items-center justify-center border border-[#F2E6E7] transition-transform duration-200 active:scale-90"
        aria-label="Favoritar"
      >
        <Heart size={16} className={isFav ? "fill-[#C58B90] text-[#C58B90]" : "text-[#C58B90]"} />
      </button>

      <button
        type="button"
        onClick={() => setPreviewOpen(true)}
        data-testid={`sticker-open-preview-${sticker.id}`}
        aria-label={`Ver ${sticker.name} maior`}
        className="sticker-canvas relative rounded-2xl aspect-square flex items-center justify-center overflow-hidden cursor-zoom-in"
      >
        <img
          src={shown}
          alt={sticker.name}
          loading="lazy"
          decoding="async"
          onLoad={() => setLoadedSrc(shown)}
          data-color={wanted ? current.label : undefined}
          data-testid={`sticker-img-${sticker.id}`}
          className={`h-[86%] w-[86%] object-contain transition-opacity duration-150 ${loading && loadedSrc ? "opacity-60" : "opacity-100"}`}
        />
        {loading && loadedSrc && (
          <Loader2 size={20} className="absolute animate-spin text-[#C58B90]" data-testid={`sticker-loading-${sticker.id}`} />
        )}
      </button>

      <p className="mt-2 text-sm font-medium text-center truncate px-1" title={displayName(sticker.name)}>
        {displayName(sticker.name) || "\u00A0"}
      </p>
      <p className="text-[11px] text-center text-[#7A7571] -mt-0.5" data-testid={`sticker-color-label-${sticker.id}`}>
        {current.label}
      </p>

      <Swatches sticker={sticker} options={options} selKey={current.key} onSelect={setSelKey} />

      <div className="mt-auto pt-1.5 flex items-center gap-2">
        <button
          data-testid={`sticker-copy-${sticker.id}`}
          onClick={handleCopy}
          disabled={copying}
          className="flex-1 h-10 rounded-full bg-[#2C2A29] text-white text-sm font-bold flex items-center justify-center gap-1.5 transition-[background-color,transform] duration-200 hover:bg-black active:scale-[0.97] disabled:opacity-70"
        >
          {copying ? <Loader2 size={15} className="animate-spin" /> : copied ? <Check size={15} /> : <Copy size={15} />}
          {copied ? "COPIADO" : "COPIAR"}
        </button>
        <button
          data-testid={`sticker-download-${sticker.id}`}
          onClick={handleDownload}
          className="h-10 w-10 shrink-0 rounded-full bg-[#F7EBEC] text-[#B07A7F] flex items-center justify-center transition-[background-color] duration-200 hover:bg-[#EFD9DB]"
          aria-label="Baixar"
          title="Baixar"
        >
          <Download size={16} />
        </button>
      </div>

      {previewOpen && (
        <StickerPreview
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          sticker={sticker}
          src={fullUrls[current.key] || shown}
          loading={!fullUrls[current.key] && (!wanted || loadedSrc !== shown)}
          colorLabel={current.label}
          swatches={<Swatches sticker={sticker} options={options} selKey={current.key} onSelect={setSelKey} prefix="preview" />}
          bgKey={bgKey}
          onBg={setBgKey}
          onCopy={handleCopy}
          copying={copying}
          copied={copied}
          onDownload={handleDownload}
        />
      )}
      {help.open && (
        <StickerHelpSheet
          open={help.open}
          onOpenChange={(o) => setHelp((h) => ({ ...h, open: o }))}
          stickerId={sticker.id}
          blob={help.blob}
          fileName={fileName}
          copied={help.copied}
        />
      )}
    </div>
  );
}

export const StickerCard = memo(StickerCardBase);
